import React, { CSSProperties, ReactNode } from "react";
import { styled } from "@mui/material/styles";
import { Paper, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "none",
}));

const StyledTable = styled(Table)(() => ({
  tableLayout: "fixed",
  wordWrap: "break-word",
}));

const StyledPaper = styled(Paper)(() => ({
  marginTop: "40px",
  marginBottom: "40px",
}));

const AlternatingRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const Title = styled(Typography)(() => ({
  padding: "10px",
}));

export interface MultiColumnTableProps {
  title: string;
  renderers: ((row: object) => ReactNode)[];
  styles: CSSProperties[];
  readonly rows?: ReadonlyArray<object | null | undefined> | null;
}

/**
 * Creates a table with multiple columns.
 * @param title the table title
 * @param renderers a list of functions, one for each column, that take a row and return a ReactNode.
 * @param styles a style for each column
 * @param rows an array of objects. Each object is used by the `renderers` to render the cell contents of a row.
 * @returns a react node that represents a table.
 */
const MultiColumnTable: React.FC<MultiColumnTableProps> = ({
  title,
  renderers,
  styles,
  rows,
}: MultiColumnTableProps) => {
  return (
    <StyledPaper>
      <Title>
        <strong>{title}</strong>
      </Title>
      <StyledTable stickyHeader size="small">
        <TableBody>
          {rows?.map(
            (row, index) =>
              row && (
                <AlternatingRow key={index}>
                  {renderers.map((renderer, index) => (
                    <StyledTableCell style={styles[index]} key={index}>
                      {renderer(row)}
                    </StyledTableCell>
                  ))}
                </AlternatingRow>
              ),
          )}
        </TableBody>
      </StyledTable>
    </StyledPaper>
  );
};

export default MultiColumnTable;
